/**
 * Lo que el navegador y las funciones de borde tienen que decir IGUAL sobre el
 * tope mensual de IA.
 *
 * Sin imports a propósito: las funciones de Deno lo importan por ruta relativa
 * (supabase/functions/_shared/usoIA.ts → ../../../services/aiAllowance.ts), igual
 * que archive-signed-contract importa services/contractFieldGeometry.ts. Si el
 * código del tope viviera escrito dos veces, el día que uno cambie el otro deja
 * de reconocerlo y el contratista vuelve a ver «el asistente está ocupado» en
 * vez de «se alcanzó el límite».
 */

/** Lo que manda el servidor en `code` cuando la IA está en pausa por el tope del mes. */
export const AI_ALLOWANCE_CODE = 'ai_allowance_exhausted';

/**
 * El `error` legible de esa misma respuesta.
 *
 * Lo leen tal cual los navegadores que todavía corren la versión anterior de la
 * app: aiChat.ts lo pinta en la burbuja y functions.ts en un aviso. Por eso va
 * en los dos idiomas y SIN ninguna palabra que el patrón de reintento de
 * aiRetryHelpers reconozca («try again», «temporarily», «rate limit», «429»):
 * con una de ellas, cada clic se convertía en tres llamadas y acababa en
 * «el asistente está ocupado», que es mentira.
 */
export const AI_ALLOWANCE_SERVER_MESSAGE =
    'La IA está en pausa hasta el día 1: se alcanzó el límite de uso de este mes. / ' +
    'AI is paused until the 1st: this month’s usage limit has been reached.';

/** 401 de las funciones de IA: sin sesión válida. Misma regla que arriba —
 *  lo leen tal cual los navegadores viejos, así que nada de palabras de
 *  reintento ni de «401»/«403», que el cliente toma por una llave rota. */
export const AI_UNAUTHENTICATED_SERVER_MESSAGE =
    'Vuelve a iniciar sesión para usar la IA. / Sign in again to use AI.';

/** 403 de las funciones de IA: la cuenta no es miembro de ese taller. */
export const AI_WORKSPACE_FORBIDDEN_SERVER_MESSAGE =
    'Esta cuenta no puede usar la IA en este taller. / This account can’t use AI in this workspace.';

export interface TokensDeUso {
    tokensEntrada: number;
    tokensSalida: number;
}

/**
 * Tokens de una respuesta de Gemini `generateContent`, tal y como los cobra
 * Google:
 *   entrada = prompt + lo que devolvieron las herramientas;
 *   salida  = texto de respuesta + «pensamiento». Los modelos 2.5 y 3.x piensan
 *             antes de contestar y ese pensamiento se factura como salida,
 *             aunque no se vea.
 * Si solo viene el total, la salida es total − entrada. Cualquier valor raro
 * cuenta 0: vale más apuntar de menos una llamada que perder el registro entero.
 */
export function tokensDeGemini(respuesta: unknown): TokensDeUso {
    const uso = (respuesta as { usageMetadata?: Record<string, unknown> } | null)?.usageMetadata ?? {};
    const n = (clave: string): number => {
        const v = Number(uso[clave]);
        return Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
    };
    const tokensEntrada = n('promptTokenCount') + n('toolUsePromptTokenCount');
    let tokensSalida = n('candidatesTokenCount') + n('thoughtsTokenCount');
    if (tokensSalida === 0 && n('totalTokenCount') > tokensEntrada) {
        tokensSalida = n('totalTokenCount') - tokensEntrada;
    }
    return { tokensEntrada, tokensSalida };
}
